/**
 * OrderBody — Trioty order-detail drawer body (single column). Sections, in
 * order, matching the design: Customer (avatar + WhatsApp/Call) · Delivery
 * address · Buyer note · Items · Shipment tracking · Payment summary · Timeline
 * (from real statusHistory). Actions live in the drawer footer.
 */

import { useState } from "react";
import { useToast } from "@a2z/ui";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { SellerOrderDetail } from "@/lib/api/types";
import { formatCurrency } from "@/lib/formatting/currency";
import { getOrderItemImageUrl } from "@/lib/order-image";
import { resolveItemTitle } from "@/modules/orders/lib/resolve-item-title";
import { ImageThumb } from "@/shared/components/PlaceholderImage";
import { avatarInitials } from "@/modules/orders/lib/order-status-display";
import { CANCELLED_STATUSES } from "@/modules/orders/lib/order-money";
import { useOrderProofUrlsById } from "@/modules/orders/services/useOrderProofQuery";
import { OnlinePaymentPanel } from "./OnlinePaymentPanel";
import { OrderStepper } from "../order-row/OrderStepper";

export type OrderBodyProps = {
  order: SellerOrderDetail;
  storeSlug: string;
};

const AVATAR_HUES = ["#B45309", "#15803D", "#2563EB", "#7C3AED", "#DC2626", "#0D9488"] as const;
function avatarTint(seed: string): { bg: string; fg: string } {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  const fg = AVATAR_HUES[Math.abs(hash) % AVATAR_HUES.length];
  return { fg, bg: `color-mix(in srgb, ${fg} 16%, var(--card))` };
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-GB", {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function OrderBody({ order, storeSlug }: OrderBodyProps) {
  const t = useTranslations();
  const locale = useLocale();
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const proofUrlsById = useOrderProofUrlsById({
    storeSlug,
    orderId: order.id,
    enabled: order.paymentProofs.length > 0,
  });

  const avatar = avatarTint(order.customer.name || order.id);
  const waDigits = order.customer.phoneE164.replace(/\D/g, "");

  const subtotal = order.items.reduce((s, it) => s + it.unitPrice * it.quantity, 0);
  const payMethodLabel =
    order.paymentMethod === "COD"
      ? t(keys.orders.paymentMethodCod)
      : t(keys.orders.paymentMethodExternalOnline);

  const reasonNote =
    order.status === "cancelled"
      ? order.cancelReasonNote
      : order.status === "declined"
        ? order.declineReasonNote
        : null;

  // Balance is against the full order total; requiredAmount is only the
  // upfront deposit(+shipping) and would understate what the customer owes.
  const balanceDue = Math.max(0, order.totalAmount - order.paidAmount);
  // No paid/balance tiles on cancelled or declined orders — nothing is owed.
  const showTiles =
    order.status !== "draft" &&
    !CANCELLED_STATUSES.has(order.status) &&
    (order.paidAmount > 0 || balanceDue > 0);

  // Address label/value rows (structured address, else the composed fallback).
  const addressRows: { label: string; value: string }[] = order.address
    ? [
        { label: t(keys.orders.addressAreaLabel), value: order.address.area },
        { label: t(keys.orders.addressStreetLabel), value: order.address.street },
        { label: t(keys.orders.addressBuildingLabel), value: order.address.building },
        { label: t(keys.orders.addressFloorLabel), value: order.address.floor ?? "" },
        { label: t(keys.orders.addressApartmentLabel), value: order.address.apartment ?? "" },
        { label: t(keys.orders.addressLandmarkLabel), value: order.address.landmark ?? "" },
      ].filter((r) => r.value.trim().length > 0)
    : [{ label: t(keys.orders.drawerAddress), value: order.buyerAddress }];


  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(order.buyerAddress);
      setCopied(true);
      toast.success(t(keys.orders.copyAddressAction));
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard denied — no-op */
    }
  };

  return (
    <div className="flex flex-col">
      {/* ── Customer ─────────────────────────────────────────────── */}
      <section className="border-b border-[color:var(--hair)] px-4 pb-[15px] pt-4">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full text-[15px] font-bold"
            style={{ background: avatar.bg, color: avatar.fg }}
          >
            {avatarInitials(order.customer.name || "?")}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[14px] font-bold text-[color:var(--text-primary)]">
              {order.customer.name || "—"}
            </div>
            {order.customer.phoneE164 ? (
              <div dir="ltr" className="mt-0.5 text-[12.5px] tabular-nums text-[color:var(--text-muted)]">
                {order.customer.phoneE164}
              </div>
            ) : null}
          </div>
        </div>
        {order.customer.phoneE164 ? (
          <div className="mt-3 flex gap-2">
            <a
              href={`https://wa.me/${waDigits}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex flex-1 items-center justify-center gap-[7px] rounded-[10px] bg-[#1FA855] px-3 py-[9px] text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm5.8 14.16c-.24.68-1.42 1.31-1.95 1.36-.53.05-1.02.24-3.43-.72-2.9-1.14-4.74-4.13-4.88-4.32-.14-.19-1.17-1.55-1.17-2.96 0-1.41.74-2.1 1-2.39.26-.29.57-.36.76-.36.19 0 .38 0 .55.01.18.01.41-.07.64.49.24.57.81 1.98.88 2.12.07.14.12.31.02.5-.09.19-.14.31-.28.48-.14.17-.29.37-.42.5-.14.14-.28.29-.12.57.16.28.72 1.19 1.55 1.93 1.06.95 1.96 1.24 2.24 1.38.28.14.44.12.6-.07.16-.19.69-.81.88-1.09.19-.28.37-.23.62-.14.25.09 1.6.76 1.87.9.28.14.46.21.53.33.07.12.07.68-.17 1.36Z" />
              </svg>
              {t(keys.orders.whatsappAction)}
            </a>
            <a
              href={`tel:${order.customer.phoneE164}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex flex-1 items-center justify-center gap-[7px] rounded-[10px] border border-[color:var(--border-strong)] bg-[color:var(--soft2)] px-3 py-[9px] text-[12.5px] font-semibold text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--soft)]"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
              </svg>
              {t(keys.orders.callAction)}
            </a>
          </div>
        ) : null}
      </section>

      {/* ── Cancel / decline reason ──────────────────────────────── */}
      {reasonNote ? (
        <section className="border-b border-[color:var(--hair)] px-4 py-[13px]">
          <div
            className="flex items-start gap-2.5 rounded-[10px] px-3 py-2.5"
            style={{ background: "rgba(239,68,68,.08)" }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--error-500)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-px shrink-0">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v5M12 16h.01" />
            </svg>
            <div className="min-w-0">
              <div className="text-[11.5px] font-semibold text-[color:var(--error-500)]">
                {t(
                  order.status === "cancelled"
                    ? keys.orders.actionCancelTitle
                    : keys.orders.actionDeclineTitle,
                )}
              </div>
              <div dir="auto" className="mt-0.5 text-[12.5px] text-[color:var(--text-primary)]">
                {reasonNote}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Delivery address ─────────────────────────────────────── */}
      <section className="border-b border-[color:var(--hair)] px-4 pb-[14px] pt-[15px]">
        <div className="mb-3 flex items-center justify-between gap-2.5">
          <span className="text-[13.5px] font-bold text-[color:var(--text-primary)]">
            {t(keys.orders.sectionDeliveryAddress)}
          </span>
          <button
            type="button"
            onClick={copyAddress}
            className="inline-flex items-center gap-1.5 rounded-[8px] border border-[color:var(--border-strong)] bg-[color:var(--soft2)] px-2.5 py-1.5 text-[11.5px] font-semibold text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              {copied ? (
                <path d="M20 6 9 17l-5-5" />
              ) : (
                <>
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </>
              )}
            </svg>
            {t(keys.orders.copyAddressAction)}
          </button>
        </div>
        <div className="flex flex-col gap-[9px]">
          {addressRows.map((row) => (
            <div key={row.label} className="flex items-baseline gap-3">
              <span className="w-[74px] shrink-0 text-[11.5px] text-[color:var(--text-muted)]">
                {row.label}
              </span>
              <span dir="auto" className="min-w-0 flex-1 text-[13px] font-semibold text-[color:var(--text-primary)]">
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Buyer note (when present) ────────────────────────────── */}
      {order.buyerNote ? (
        <section className="border-b border-[color:var(--hair)] px-4 pb-[14px] pt-[15px]">
          <div className="mb-1.5 text-[11.5px] font-semibold text-[color:var(--text-muted)]">
            {t(keys.orders.buyerNoteLabel)}
          </div>
          <div dir="auto" className="text-[13px] text-[color:var(--text-primary)]">
            {order.buyerNote}
          </div>
        </section>
      ) : null}

      {/* ── Items ────────────────────────────────────────────────── */}
      <section className="border-b border-[color:var(--hair)]">
        <div className="px-4 pb-[9px] pt-[15px] text-[13.5px] font-bold text-[color:var(--text-primary)]">
          {t(keys.orders.itemsTitle)}
        </div>
        {order.items.map((item, i) => {
          const title = resolveItemTitle(item, locale);
          const variantLine = item.selectedOptions
            .map((opt) => (locale === "ar" ? opt.value.ar : (opt.value.en ?? opt.value.ar)))
            .filter(Boolean)
            .join(" · ");
          return (
            <div
              key={`${item.productId}-${i}`}
              className="flex items-center gap-3 border-t border-[color:var(--hair)] px-4 py-[11px]"
            >
              <ImageThumb
                src={getOrderItemImageUrl(item)}
                seed={item.productId}
                alt={title}
                size="h-[40px] w-[40px]"
                bg="bg-white"
                className="rounded-[10px]"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold text-[color:var(--text-primary)]">
                  {title}
                </div>
                {variantLine ? (
                  <div className="truncate text-[11.5px] text-[color:var(--text-muted)]">
                    {variantLine}
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2.5">
                {item.quantity > 1 ? (
                  <span className="rounded-[7px] bg-[color:var(--soft2)] px-[7px] py-0.5 text-[11px] font-bold text-[color:var(--text-secondary)]">
                    ×{item.quantity}
                  </span>
                ) : null}
                <span className="text-[13px] font-bold text-[color:var(--text-primary)]">
                  {formatCurrency(item.unitPrice * item.quantity, locale)}
                </span>
              </div>
            </div>
          );
        })}
      </section>

      {/* ── Payment proof (when uploaded) ────────────────────────── */}
      {order.paymentProofs.length > 0 ? (
        <section className="border-b border-[color:var(--hair)] px-4 pb-[13px] pt-[15px]">
          <div className="mb-2.5 text-[13.5px] font-bold text-[color:var(--text-primary)]">
            {t(keys.orders.drawerProofTitle)}
          </div>
          <div className="flex flex-wrap gap-2.5">
            {order.paymentProofs.map((proof) => {
              // Proofs live in the private bucket; the signed URL arrives with
              // the proof query, so a tile is inert until its URL lands.
              const url = proofUrlsById.get(proof.id) ?? null;
              const tile =
                "flex items-center gap-2.5 rounded-[10px] border border-[color:var(--card-br)] bg-[color:var(--soft2)] p-1.5 pe-3 transition-colors";
              const amount = (
                <span className="text-[12.5px] font-semibold text-[color:var(--text-primary)]">
                  {formatCurrency(proof.paidAmount, locale)}
                </span>
              );
              if (!url) {
                return (
                  <span key={proof.id} className={tile}>
                    <span className="h-[40px] w-[40px] rounded-[8px] bg-[color:var(--soft)]" />
                    {amount}
                  </span>
                );
              }
              return (
                <a
                  key={proof.id}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={`${tile} hover:bg-[color:var(--soft)]`}
                >
                  <img src={url} alt="" className="h-[40px] w-[40px] rounded-[8px] object-cover" />
                  {amount}
                </a>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* ── Shipment tracking (when present) ─────────────────────── */}
      {order.shipment?.trackingNumber ? (
        <section className="flex items-center justify-between gap-3 border-b border-[color:var(--hair)] px-4 py-[13px]">
          <div className="min-w-0">
            <div className="text-[11.5px] text-[color:var(--text-muted)]">
              {t(keys.orders.drawerDispatched)}
            </div>
            <div dir="ltr" className="truncate font-mono text-[13px] font-semibold text-[color:var(--text-primary)]">
              {order.shipment.trackingNumber}
            </div>
          </div>
          {order.shipment.trackingUrl ? (
            <a
              href={order.shipment.trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 rounded-[8px] border border-[color:var(--border-strong)] bg-[color:var(--soft2)] px-2.5 py-1.5 text-[11.5px] font-semibold text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
            >
              {t(keys.orders.drawerOpenTracking)}
            </a>
          ) : null}
        </section>
      ) : null}

      {/* ── Payment summary ──────────────────────────────────────── */}
      <section className="border-b border-[color:var(--hair)] px-4 py-[15px]">
        <div className="mb-[11px] flex items-center justify-between">
          <span className="text-[12.5px] font-bold text-[color:var(--text-primary)]">
            {t(keys.orders.drawerPayment)}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-[8px] border border-[color:var(--border-strong)] bg-[color:var(--soft2)] px-2.5 py-1 text-[11.5px] font-semibold text-[color:var(--text-secondary)]">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <path d="M2 10h20" />
            </svg>
            {payMethodLabel}
          </span>
        </div>
        <div className="mb-[11px] -mx-4 h-px bg-[color:var(--hair)]" />
        <div className="mb-[9px] flex items-center justify-between text-[12.5px] text-[color:var(--text-secondary)]">
          <span>{t(keys.orders.drawerSubtotal)}</span>
          <span className="font-semibold text-[color:var(--text-primary)]">
            {formatCurrency(subtotal, locale)}
          </span>
        </div>
        {order.couponDiscountAmount > 0 ? (
          <div className="mb-[9px] flex items-center justify-between text-[12.5px] text-[color:var(--text-secondary)]">
            <span className="truncate">
              {order.couponCode ? `${t(keys.orders.couponLabel)} · ${order.couponCode}` : t(keys.orders.couponLabel)}
            </span>
            <span className="font-semibold text-[color:var(--success-500)]">
              −{formatCurrency(order.couponDiscountAmount, locale)}
            </span>
          </div>
        ) : null}
        <div className="mb-[11px] flex items-center justify-between text-[12.5px] text-[color:var(--text-secondary)]">
          <span>{t(keys.orders.drawerShipping)}</span>
          <span
            className="font-semibold"
            style={{ color: order.shippingFee === 0 ? "var(--success-500)" : "var(--text-primary)" }}
          >
            {order.shippingFee === 0
              ? t(keys.orders.drawerFreeShipping)
              : formatCurrency(order.shippingFee, locale)}
          </span>
        </div>
        <div className="mb-[11px] -mx-4 h-px bg-[color:var(--line)]" />
        <div className="flex items-center justify-between">
          <span className="text-[14px] font-bold text-[color:var(--text-primary)]">
            {t(keys.orders.totalLabel)}
          </span>
          <span className="text-[18px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
            {formatCurrency(order.totalAmount, locale)}
          </span>
        </div>

        {order.status === "draft" ? (
          <div className="mt-[11px] text-[12px] text-[color:var(--text-muted)]">
            {t(keys.orders.moneyNotStarted)}
          </div>
        ) : showTiles ? (
          <>
            <div className="my-[11px] -mx-4 h-px bg-[color:var(--hair)]" />
            <div className="flex items-center justify-between text-[12.5px] text-[color:var(--text-secondary)]">
              <span>{t(keys.orders.tilePaidSoFar)}</span>
              <span className="font-semibold text-[color:var(--success-500)]">
                {formatCurrency(order.paidAmount, locale)}
              </span>
            </div>
            <div className="mt-[9px] flex items-center justify-between text-[12.5px] text-[color:var(--text-secondary)]">
              <span>{t(keys.orders.tileBalanceDue)}</span>
              <span className="font-semibold text-[color:var(--warning-500)]">
                {formatCurrency(balanceDue, locale)}
              </span>
            </div>
          </>
        ) : null}
      </section>

      {/* ── Online payment (gateway txn + refund) ───────────────── */}
      <OnlinePaymentPanel order={order} storeSlug={storeSlug} />

      {/* ── Timeline stepper ─────────────────────────────────────── */}
      <section className="px-4 pb-1 pt-[15px]">
        <div className="mb-3.5 text-[13.5px] font-bold text-[color:var(--text-primary)]">
          {t(keys.orders.statusHistoryTitle)}
        </div>
        <OrderStepper status={order.status} placedSub={formatDate(order.createdAt, locale)} />
      </section>
    </div>
  );
}
